/* =========================================================
   WWC Product Gallery
   Vervanger voor Magic Zoom Plus / Magic Scroll.

   Ontwerpregels (waarom dit stil blijft in de console):
   - Elke ingang is defensief: ontbreekt een element, dan stopt de
     functie zonder te gooien.
   - Alle listeners hangen aan een AbortController en worden bij
     disconnectedCallback in een keer opgeruimd -> geen listeners op
     verwijderde nodes bij section-rerender of quick view.
   - Geen preventDefault in passive listeners; horizontaal/verticaal
     gedrag wordt met touch-action in CSS geregeld.
   - video.play() en img.decode() geven promises terug die kunnen
     rejecten (autoplay-beleid, afgebroken load). Die worden altijd
     opgevangen, anders verschijnt er een unhandled rejection.
   - Geen eval, geen document.write, geen globale variabelen behalve
     het custom element zelf.
========================================================= */

(function () {
  'use strict';

  var TAG = 'wwc-product-gallery';

  if (!window.customElements || window.customElements.get(TAG)) return;
  if (!('AbortController' in window) || !('PointerEvent' in window)) return;

  var MAX_ZOOM = 3;
  var MIN_USEFUL_ZOOM = 1.15;
  var TAP_MOVE_PX = 8;
  var TAP_MAX_MS = 600;

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  function idle(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 1500 });
    } else {
      window.setTimeout(fn, 300);
    }
  }

  /* Werkelijk geschilderd vlak van een object-fit:contain afbeelding.
     Zonder deze correctie loopt de zoom scheef bij niet-vierkante media. */
  function paintedRect(img) {
    var box = img.getBoundingClientRect();
    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    if (!nw || !nh || !box.width || !box.height) return box;
    var scale = Math.min(box.width / nw, box.height / nh);
    var w = nw * scale;
    var h = nh * scale;
    return {
      left: box.left + (box.width - w) / 2,
      top: box.top + (box.height - h) / 2,
      width: w,
      height: h
    };
  }

  var Gallery = function () {
    return Reflect.construct(HTMLElement, [], Gallery);
  };

  Gallery.prototype = Object.create(HTMLElement.prototype);
  Gallery.prototype.constructor = Gallery;
  Object.setPrototypeOf(Gallery, HTMLElement);

  Gallery.prototype.connectedCallback = function () {
    if (this._ready) return;

    this.track = this.querySelector('[data-gal-track]');
    this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-gal-slide]'));
    if (!this.track || !this.slides.length) return;

    this._ready = true;
    this.ac = new AbortController();
    this.signal = this.ac.signal;

    this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-gal-thumb]'));
    this.thumbTrack = this.querySelector('[data-gal-thumbs-track]');
    this.live = this.querySelector('[data-gal-live]');
    this.hint = this.querySelector('[data-gal-hint]');
    this.prevBtn = this.querySelector('[data-gal-prev]');
    this.nextBtn = this.querySelector('[data-gal-next]');
    this.thumbPrev = this.querySelector('[data-gal-thumb-prev]');
    this.thumbNext = this.querySelector('[data-gal-thumb-next]');

    this.index = clamp(parseInt(this.dataset.startIndex, 10) || 0, 0, this.slides.length - 1);
    this.zoomEnabled = this.dataset.zoom !== 'false';
    this.zoomedFrame = null;
    this._rafScroll = 0;

    this.bindTrack();
    this.bindThumbs();
    this.bindNav();
    this.bindZoom();
    this.bindVariants();
    this.bindKeys();

    this.goTo(this.index, { silent: true });
    this.classList.add('wwc-gal--ready');

    var self = this;
    idle(function () {
      if (self._ready) self.prepareZoom(self.slides[self.index]);
    });
  };

  Gallery.prototype.disconnectedCallback = function () {
    if (this.ac) this.ac.abort();
    if (this._rafScroll) cancelAnimationFrame(this._rafScroll);
    this._ready = false;
  };

  /* ---------- Navigatie ---------- */

  Gallery.prototype.goTo = function (i, opts) {
    var options = opts || {};
    var target = clamp(i, 0, this.slides.length - 1);
    var slide = this.slides[target];
    if (!slide) return;

    this.closeZoom();
    this.index = target;
    /* De doelfoto meteen promoveren. Bij een sprong van thumb 1 naar 13
       staat die nog op lazy en zou je een leeg kader zien tot de browser
       zelf besluit te laden. */
    this.promote(slide);
    this.track.scrollLeft = slide.offsetLeft - this.track.offsetLeft;
    this.syncUI(options.silent);
    this.prefetchNeighbours();
  };

  Gallery.prototype.promote = function (slide) {
    if (!slide) return;
    var img = slide.querySelector('.wwc-gal__img');
    if (!img || img.loading !== 'lazy') return;
    img.loading = 'eager';
    if ('fetchPriority' in img) img.fetchPriority = 'high';
  };

  Gallery.prototype.syncUI = function (silent) {
    var i = this.index;
    var total = this.slides.length;

    for (var t = 0; t < this.thumbs.length; t++) {
      var on = Number(this.thumbs[t].dataset.index) === i;
      this.thumbs[t].setAttribute('aria-current', on ? 'true' : 'false');
      if (on) this.scrollThumbIntoView(this.thumbs[t]);
    }

    if (this.prevBtn) this.prevBtn.disabled = i === 0;
    if (this.nextBtn) this.nextBtn.disabled = i === total - 1;

    var slide = this.slides[i];
    var frame = slide ? slide.querySelector('[data-gal-frame]') : null;
    if (this.hint) {
      this.hint.hidden = !(this.zoomEnabled && frame && frame.dataset.zoomable === 'true');
    }

    this.syncThumbArrows();

    if (!silent && this.live && total > 1) {
      this.live.textContent = 'Afbeelding ' + (i + 1) + ' van ' + total;
    }
  };

  Gallery.prototype.bindTrack = function () {
    var self = this;
    this.track.addEventListener(
      'scroll',
      function () {
        if (self._rafScroll) return;
        self._rafScroll = requestAnimationFrame(function () {
          self._rafScroll = 0;
          self.readIndexFromScroll();
        });
      },
      { passive: true, signal: this.signal }
    );
  };

  Gallery.prototype.readIndexFromScroll = function () {
    var w = this.track.clientWidth;
    if (!w) return;
    var next = clamp(Math.round(this.track.scrollLeft / w), 0, this.slides.length - 1);
    if (next === this.index) return;
    this.closeZoom();
    this.index = next;
    this.syncUI();
    this.prefetchNeighbours();
  };

  Gallery.prototype.bindNav = function () {
    var self = this;
    if (this.prevBtn) {
      this.prevBtn.addEventListener(
        'click',
        function () {
          self.goTo(self.index - 1);
        },
        { signal: this.signal }
      );
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener(
        'click',
        function () {
          self.goTo(self.index + 1);
        },
        { signal: this.signal }
      );
    }
  };

  /* ---------- Thumbnails ---------- */

  Gallery.prototype.bindThumbs = function () {
    var self = this;
    for (var i = 0; i < this.thumbs.length; i++) {
      this.thumbs[i].addEventListener(
        'click',
        function (e) {
          var idx = Number(e.currentTarget.dataset.index);
          if (!isNaN(idx)) self.goTo(idx);
        },
        { signal: this.signal }
      );
    }

    if (this.thumbTrack) {
      this.thumbTrack.addEventListener(
        'scroll',
        function () {
          self.syncThumbArrows();
        },
        { passive: true, signal: this.signal }
      );
    }

    var step = function (dir) {
      if (!self.thumbTrack) return;
      self.thumbTrack.scrollBy({ left: dir * Math.max(120, self.thumbTrack.clientWidth * 0.7) });
    };

    if (this.thumbPrev) {
      this.thumbPrev.addEventListener('click', function () { step(-1); }, { signal: this.signal });
    }
    if (this.thumbNext) {
      this.thumbNext.addEventListener('click', function () { step(1); }, { signal: this.signal });
    }
  };

  Gallery.prototype.scrollThumbIntoView = function (thumb) {
    if (!this.thumbTrack) return;
    var t = this.thumbTrack;
    var left = thumb.offsetLeft - t.offsetLeft;
    var right = left + thumb.offsetWidth;
    if (left < t.scrollLeft) {
      t.scrollLeft = Math.max(0, left - 8);
    } else if (right > t.scrollLeft + t.clientWidth) {
      t.scrollLeft = right - t.clientWidth + 8;
    }
  };

  Gallery.prototype.syncThumbArrows = function () {
    if (!this.thumbTrack) return;
    var t = this.thumbTrack;
    var overflow = t.scrollWidth - t.clientWidth;
    var hasOverflow = overflow > 2;
    if (this.thumbPrev) {
      this.thumbPrev.hidden = !hasOverflow;
      this.thumbPrev.disabled = t.scrollLeft <= 1;
    }
    if (this.thumbNext) {
      this.thumbNext.hidden = !hasOverflow;
      this.thumbNext.disabled = t.scrollLeft >= overflow - 1;
    }
  };

  /* ---------- Zoom ---------- */

  Gallery.prototype.bindZoom = function () {
    if (!this.zoomEnabled) return;
    var self = this;

    for (var i = 0; i < this.slides.length; i++) {
      var frame = this.slides[i].querySelector('[data-gal-frame]');
      if (!frame || !frame.dataset.zoomSrc) continue;

      frame.addEventListener('pointerdown', function (e) { self.onDown(e); }, { signal: this.signal });
      frame.addEventListener('pointerup', function (e) { self.onUp(e); }, { signal: this.signal });
      frame.addEventListener('pointercancel', function (e) { self.onUp(e); }, { signal: this.signal });
      frame.addEventListener('pointermove', function (e) { self.onMove(e); }, { signal: this.signal });
      frame.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse') self.prepareZoom(e.currentTarget.closest('[data-gal-slide]'));
      }, { signal: this.signal });
      frame.addEventListener('pointerleave', function (e) {
        if (e.pointerType === 'mouse') self.closeZoom();
      }, { signal: this.signal });
    }
  };

  /* Maakt (eenmalig) de hoge-resolutie laag aan. Faalt de download,
     dan blijft de gewone foto staan en wordt zoom stil uitgezet. */
  Gallery.prototype.prepareZoom = function (slide) {
    if (!slide) return null;
    var frame = slide.querySelector('[data-gal-frame]');
    if (!frame || frame.dataset.zoomable !== 'true') return null;
    if (frame._zoomImg) return frame._zoomImg;

    var src = frame.dataset.zoomSrc;
    if (!src) return null;

    var img = document.createElement('img');
    img.className = 'wwc-gal__zoomimg';
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.decoding = 'async';
    img.draggable = false;

    img.addEventListener('error', function () {
      frame.dataset.zoomable = 'false';
      if (img.parentNode) img.parentNode.removeChild(img);
      frame._zoomImg = null;
    }, { once: true, signal: this.signal });

    img.src = src;
    frame.appendChild(img);
    frame._zoomImg = img;
    return img;
  };

  Gallery.prototype.prefetchNeighbours = function () {
    var self = this;
    idle(function () {
      if (!self._ready) return;
      self.promote(self.slides[self.index + 1]);
      self.promote(self.slides[self.index - 1]);
    });
  };

  Gallery.prototype.onDown = function (e) {
    var frame = e.currentTarget;
    this._down = {
      frame: frame,
      x: e.clientX,
      y: e.clientY,
      t: e.timeStamp,
      id: e.pointerId,
      type: e.pointerType
    };
    if (e.pointerType !== 'mouse') this.prepareZoom(frame.closest('[data-gal-slide]'));
  };

  Gallery.prototype.onUp = function (e) {
    var d = this._down;
    this._down = null;
    if (!d || d.id !== e.pointerId) return;

    var moved = Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y);
    var quick = e.timeStamp - d.t < TAP_MAX_MS;
    if (e.type === 'pointercancel' || moved > TAP_MOVE_PX || !quick) return;

    if (this.zoomedFrame === d.frame) {
      this.closeZoom();
    } else {
      this.openZoom(d.frame, e);
    }
  };

  Gallery.prototype.onMove = function (e) {
    var frame = e.currentTarget;
    if (this.zoomedFrame !== frame) return;
    if (e.pointerType !== 'mouse' && !this._panning) return;
    this.pan(frame, e.clientX, e.clientY);
  };

  Gallery.prototype.openZoom = function (frame, e) {
    if (!frame || frame.dataset.zoomable !== 'true') return;
    var zoomImg = frame._zoomImg || this.prepareZoom(frame.closest('[data-gal-slide]'));
    if (!zoomImg || !zoomImg.complete || !zoomImg.naturalWidth) return;

    var base = frame.querySelector('.wwc-gal__img');
    if (!base) return;

    /* paintedRect valt terug op het elementkader als de intrinsieke maat
       (nog) niet bekend is. Bij een vierkante stage en vierkante foto is
       dat hetzelfde vlak, dus zoom blijft werken in plaats van stil te
       weigeren. */
    var rect = paintedRect(base);
    if (!rect.width || !rect.height) return;

    var scale = clamp(zoomImg.naturalWidth / rect.width, 1, MAX_ZOOM);
    if (scale < MIN_USEFUL_ZOOM) {
      frame.dataset.zoomable = 'false';
      this.syncUI(true);
      return;
    }

    var frameRect = frame.getBoundingClientRect();
    zoomImg.style.left = rect.left - frameRect.left + 'px';
    zoomImg.style.top = rect.top - frameRect.top + 'px';
    zoomImg.style.width = rect.width + 'px';
    zoomImg.style.height = rect.height + 'px';

    frame._zoom = { scale: scale, rect: rect, frameRect: frameRect };
    frame.classList.add('is-zoomed');
    this.classList.add('is-zooming');
    this.zoomedFrame = frame;

    if (e && e.pointerType !== 'mouse') {
      this._panning = true;
      try {
        frame.setPointerCapture(e.pointerId);
      } catch (err) {
        /* pointer kan al weg zijn; panning werkt dan zonder capture */
      }
    }

    if (e) this.pan(frame, e.clientX, e.clientY);
  };

  Gallery.prototype.pan = function (frame, clientX, clientY) {
    var z = frame._zoom;
    var zoomImg = frame._zoomImg;
    if (!z || !zoomImg) return;

    var relX = clamp((clientX - z.rect.left) / z.rect.width, 0, 1);
    var relY = clamp((clientY - z.rect.top) / z.rect.height, 0, 1);

    var dx = -relX * z.rect.width * (z.scale - 1);
    var dy = -relY * z.rect.height * (z.scale - 1);

    zoomImg.style.transform =
      'translate3d(' + dx.toFixed(2) + 'px,' + dy.toFixed(2) + 'px,0) scale(' + z.scale + ')';
  };

  Gallery.prototype.closeZoom = function () {
    var frame = this.zoomedFrame;
    this._panning = false;
    if (!frame) return;
    frame.classList.remove('is-zoomed');
    this.classList.remove('is-zooming');
    if (frame._zoomImg) frame._zoomImg.style.transform = '';
    frame._zoom = null;
    this.zoomedFrame = null;
  };

  /* ---------- Varianten ---------- */

  Gallery.prototype.bindVariants = function () {
    var raw = this.querySelector('[data-gal-variant-media]');
    if (!raw) return;

    var map = {};
    try {
      map = JSON.parse(raw.textContent || '{}') || {};
    } catch (err) {
      return; /* stil: liever geen variantsprong dan een fout */
    }

    var self = this;
    document.addEventListener(
      'variant:change',
      function (e) {
        var variant = e && e.detail && e.detail.variant;
        if (!variant || !variant.id) return;
        var mediaId = map[String(variant.id)];
        if (!mediaId) return;
        var idx = self.slides.findIndex(function (s) {
          return String(s.dataset.mediaId) === String(mediaId);
        });
        if (idx >= 0 && idx !== self.index) self.goTo(idx);
      },
      { signal: this.signal }
    );
  };

  /* ---------- Toetsenbord ---------- */

  Gallery.prototype.bindKeys = function () {
    var self = this;
    this.addEventListener(
      'keydown',
      function (e) {
        if (e.key === 'Escape' && self.zoomedFrame) {
          self.closeZoom();
          return;
        }
        if (e.target && e.target.closest && e.target.closest('[data-gal-thumbs-track]')) return;
        if (e.key === 'ArrowLeft') {
          self.goTo(self.index - 1);
        } else if (e.key === 'ArrowRight') {
          self.goTo(self.index + 1);
        }
      },
      { signal: this.signal }
    );
  };

  window.customElements.define(TAG, Gallery);
})();
